<template>
  <form
    id="icon-family-style"
    action=""
    class="icon-details-styling"
    @submit.prevent
  >
    <div
      v-if="!isNotShowingFamilyAndStyles"
      class="margin-bottom-xl"
      style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md)"
    >
      <div
        v-if="majorVersion !== 5"
        style=""
      >
        <wa-select-custom
          :value="selectedPack"
          :options="iconPacks"
          :custom-style="selectCustomStyle"
          :custom-select-style="selectCustomSelectStyle"
          option-label-prop="label"
          icon-name="faSwatchbook"
          is-minimal
          label="Icon Family"
          @change="(value) => updatePack(value)"
        />
      </div>
      <div style="">
        <span
          id="icon_style_label"
          class="sr-only"
          >Select an Icon Style</span
        >
        <div
          id="icon_style"
          aria-labelledby="icon_style_label"
          class="buttons select-icon-style"
          style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr"
        >
          <button
            v-for="style in iconStyles"
            :key="`${style.family}-${style.style}`"
            :aria-label="getLabel(style)"
            :style="styleSelectButtonStyle(style.family, style.style)"
            :class="[
              'select-icon-style-button margin-top-0 compact',
              {
                active:
                  styling.style === 'duotone'
                    ? style.family === styling.style && style.style === 'solid'
                    : style.style === styling.style && style.family === styling.family
              }
            ]"
            data-balloon-pos="up"
            @click.prevent="updateFamilyStyle(style.style, style.family)"
          >
            <i :class="iconClass(style.family, style.style)" />
          </button>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
  import { IconLabelMixin } from 'app/views/icons/mixins.js'

  export default {
    name: 'IconPackSelector',

    mixins: [IconLabelMixin],

    props: {
      icon: {
        type: Object
      },
      styling: {
        type: Object,
        required: true
      },
      majorVersion: {
        type: Number
      }
    },

    data() {
      return {
        selectedPack: 'classic'
      }
    },

    computed: {
      isNotShowingFamilyAndStyles() {
        return this.styling.family === 'brands' || this.styling.style === 'custom'
      },

      iconPacks() {
        if (this.icon !== null) {
          if (this.majorVersion === 5) {
            // v5 doesn't have a Icon Pack dropdown so we put all familyStyles together
            const familyStyles = this.icon.icon_packs_list.flatMap((pack) => pack.family_styles)

            return [
              {
                value: 'classic',
                name: 'classic',
                label: 'Classic',
                familyStyles: familyStyles
              }
            ]
          } else {
            const packs = this.icon.icon_packs_list.reduce((acc, pack) => {
              acc.push({
                value: pack.pack,
                name: pack.pack,
                label: pack.label,
                familyStyles: pack.family_styles
              })
              return acc
            }, [])

            return packs
          }
        } else {
          return []
        }
      },

      selectCustomStyle() {
        return {
          '--input-with-icon-color': 'var(--text-color)'
        }
      },

      selectCustomSelectStyle() {
        return {
          '--input-border-color': 'var(--fa-gravy)',
          'font-weight': 'var(--font-weight-normal)',
          'padding-top': 'var(--spacing-sm)',
          'padding-bottom': 'var(--spacing-sm)'
        }
      },

      currentPack() {
        return this.iconPacks.find((pack) => pack.name === this.selectedPack)
      },

      packDefaultFamily() {
        return this.currentPack?.familyStyles[0].family
      },

      packDefaultStyle() {
        return this.currentPack?.familyStyles[0].style
      },

      iconStyles() {
        return this.currentPack?.familyStyles.filter((familyStyle) => familyStyle.style != 'brands')
      }
    },

    watch: {
      icon() {
        this.setSelectedPack()
      }
    },

    created() {
      this.setSelectedPack()
    },

    methods: {
      getLabel(style) {
        if (this.majorVersion === 5 && style.family === 'duotone' && style.style === 'solid') {
          return 'Duotone Solid'
        }

        return this.getFamilyStyleLabel(style.family, style.style)
      },

      setSelectedPack() {
        const pack = this.iconPacks.find((pack) => pack.familyStyles.some((fs) => fs.family === this.icon.family && fs.style === this.icon.style))

        if (pack) {
          this.selectedPack = pack.name
        }
      },

      updatePack(packName) {
        this.selectedPack = packName
        this.$emit('change', { ...this.styling, ...{ family: this.packDefaultFamily, style: this.packDefaultStyle } })
      },

      updateFamilyStyle(style, family) {
        if (this.majorVersion === 5 && family === 'duotone' && style === 'solid') {
          this.$emit('change', { ...this.styling, ...{ family: 'classic', style: 'duotone' } })
        } else {
          this.$emit('change', { ...this.styling, ...{ family: family, style: style } })
        }
      },

      iconClass(family, style) {
        if (this.majorVersion === 5 && family === 'duotone' && style === 'solid') {
          return `fad fa-${this.icon.name} fa-lg`
        } else if (this.majorVersion === 5) {
          return `fa${style.charAt(0)} fa-${this.icon.name} fa-xl`
        } else {
          const classString = `fa-${style} fa-${this.icon.name} fa-xl`
          return family === 'classic' ? classString : `fa-${family} ${classString}`
        }
      },

      styleSelectButtonStyle(iconFamily, iconStyle) {
        let style = {}

        const isSelected = iconStyle === this.styling.style && this.styling.family === iconFamily

        if (isSelected) {
          style['--button-hover-color'] = 'var(--text-color)'
          style['--button-hover-background'] = 'var(--fa-gravy)'
        } else {
          style['--button-hover-color'] = 'var(--link-hover-color)'
          style['--button-hover-background'] = 'transparent'
        }

        return {
          ...style,
          ...{
            '--button-border-radius': 'var(--input-border-radius)',
            '--button-border-color': 'var(--fa-gravy)',
            '--button-box-shadow': 'none',
            '--button-box-shadow-width': '0',
            '--button-padding': 'var(--spacing-sm) var(--spacing-lg)',
            '--button-color': 'var(--link-color)',
            '--button-active-border-color': 'var(--fa-gravy)',
            '--button-active-background': 'var(--fa-gravy)',
            '--button-active-color': 'var(--text-color)'
          }
        }
      }
    }
  }
</script>
